import { useCallback, useMemo } from 'react';

import { useHideQuestion } from '../data/useHideQuestion';
import { useUnhideQuestion } from '../data/useUnhideQuestion';
import { findNode } from '../model/templateProjection';
import { failureFor } from '../state/selectors';
import { LABEL_KEYS } from '../viewmodels/messages';
import { runOperation, type RunOperationDeps } from './runOperation';

export type QuestionActions = {
  hideQuestion: (questionId: string) => Promise<void>;
  unhideQuestion: (questionId: string) => Promise<void>;
  retry: (questionId: string) => Promise<void>;
};

export function useQuestionActions(deps: RunOperationDeps): QuestionActions {
  const { getLatestState } = deps;
  const hide = useHideQuestion();
  const unhide = useUnhideQuestion();

  const hideQuestion = useCallback(
    async (questionId: string) => {
      const { templateId, channelId } = getLatestState();

      await runOperation(
        {
          op: {
            kind: 'hideQuestion',
            targetId: questionId,
            change: { kind: 'hideQuestion', itemId: questionId },
          },
          execute: context =>
            hide({ channelId, templateId, itemId: questionId }, context),
          successMessage: { key: LABEL_KEYS.hideQuestionSucceededToast },
        },
        deps
      );
    },
    [getLatestState, hide, deps]
  );

  const unhideQuestion = useCallback(
    async (questionId: string) => {
      const { templateId, channelId, replica } = getLatestState();
      const node = replica ? findNode(replica, questionId) : undefined;

      if (node?.type !== 'item') {
        return;
      }

      const { section, item } = node;

      await runOperation(
        {
          op: {
            kind: 'unhideQuestion',
            targetId: questionId,
            change: { kind: 'unhideQuestion', itemId: questionId },
          },
          execute: context =>
            unhide(
              { channelId, templateId, sectionId: section.id, item },
              context
            ),
          successMessage: { key: LABEL_KEYS.unhideQuestionSucceededToast },
        },
        deps
      );
    },
    [getLatestState, unhide, deps]
  );

  const retry = useCallback(
    async (questionId: string) => {
      const failed = failureFor(getLatestState(), questionId);
      const kind = failed?.op.kind;

      if (kind === 'hideQuestion') {
        await hideQuestion(questionId);
      }

      if (kind === 'unhideQuestion') {
        await unhideQuestion(questionId);
      }
    },
    [getLatestState, hideQuestion, unhideQuestion]
  );

  return useMemo(
    () => ({ hideQuestion, unhideQuestion, retry }),
    [hideQuestion, unhideQuestion, retry]
  );
}
